import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { normalize } from '../main-page/home-utils';
import { TERMS } from './terms-data';
import '../../hs-theme.css';
import '../guide/guide.css';
import './terms-page.css';

// an article counts as reached when its top is this close to the top of the window
const ACTIVE_OFFSET = 140;
// the back to top button shows up once the reader is this far down
const TOP_BUTTON_FROM = 700;
const MOBILE_WIDTH = 900;
const MARKS = { dot: '•', check: '✓', ban: '×' };

const articleId = (num) => `clan-${num}`;

// all the text of an article, so the search also finds words inside lists and definitions
const searchText = ([num, title, blocks]) => normalize([num, title, ...blocks.map(block =>
    block.type === 'defs' ? block.items.flat().join(' ') : block.type === 'list' ? block.items.join(' ') : block.text
)].join(' '));
const INDEX = TERMS.map(searchText);

const Block = ({ block }) => {
    if (block.type === 'list') {
        return (
            <div className='hs-terms-list'>
                {block.items.map(item => (
                    <div key={item}>
                        <span className={`hs-terms-mark ${block.mark}`}>{MARKS[block.mark]}</span>
                        <span>{item}</span>
                    </div>
                ))}
            </div>
        );
    }
    if (block.type === 'defs') {
        return (
            <div className='hs-terms-defs'>
                {block.items.map(([term, definition]) => (
                    <div key={term}>
                        <span className='hs-terms-term'>{term}</span>
                        <span>{definition}</span>
                    </div>
                ))}
            </div>
        );
    }
    if (block.type === 'note') {
        return (
            <div className='hs-terms-note'>
                <span className='hs-terms-note-icon'>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM12 8v5M12 16h.01" /></svg>
                </span>
                <p>{block.text}</p>
            </div>
        );
    }
    return <p className='hs-terms-text'>{block.text}{block.placeholder && <> <mark>{block.placeholder}</mark></>}</p>;
};

export const TermsPage = () => {
    const { t } = useTranslation(["terms"]);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(articleId(TERMS[0][0]));
    const [showTop, setShowTop] = useState(false);
    const [mobile, setMobile] = useState(() => window.innerWidth < MOBILE_WIDTH);
    // on phones the contents list is folded under a button
    const [tocOpen, setTocOpen] = useState(false);

    const shown = useMemo(() => {
        const q = normalize(query.trim());
        return TERMS.filter((article, i) => !q || INDEX[i].includes(q));
    }, [query]);

    useEffect(() => {
        const onResize = () => setMobile(window.innerWidth < MOBILE_WIDTH);
        // the contents list marks the article being read
        const onScroll = () => {
            let current = articleId(TERMS[0][0]);
            TERMS.forEach(([num]) => {
                const el = document.getElementById(articleId(num));
                if (el && el.getBoundingClientRect().top < ACTIVE_OFFSET) current = articleId(num);
            });
            setActive(current);
            setShowTop(window.scrollY > TOP_BUTTON_FROM);
        };
        onScroll();
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    const jump = (id) => (e) => {
        e.preventDefault();
        if (mobile) setTocOpen(false);
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className='hs hs-article hs-terms'>
            <section className='hs-article-intro'>
                <div className='hs-article-intro-inner'>
                    <nav className='hs-article-crumbs'>
                        <Link to='/'>{t("crumb_home")}</Link><span>/</span>
                        <span className='current'>{t("crumb_terms")}</span>
                    </nav>
                    <p className='hs-terms-eyebrow'>{t("eyebrow")}</p>
                    <h1>{t("title")}</h1>
                    <p className='hs-article-lead'>{t("lead")}</p>
                    <div className='hs-terms-chips'>
                        <span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1ZM4 10h16M8 3v4M16 3v4" /></svg>
                            {t("in_force")}
                        </span>
                        <span>{t("article_count", { n: TERMS.length })}</span>
                        <span className='blue'>{t("country")}</span>
                    </div>
                </div>
            </section>

            <div className='hs-terms-body'>
                <aside className='hs-terms-toc'>
                    <label className='hs-terms-search'>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B6779" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14ZM20 20l-3.5-3.5" /></svg>
                        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search")} />
                        {query && <button onClick={() => setQuery('')} aria-label={t("clear_search")}>×</button>}
                    </label>
                    {mobile &&
                        <button className='hs-terms-toc-toggle' onClick={() => setTocOpen(!tocOpen)} aria-expanded={tocOpen}>
                            {t("contents", { n: shown.length })}
                            <svg className={tocOpen ? 'open' : ''} width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5 6 6.5 11 1.5" stroke="#5B6779" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>}
                    {(!mobile || tocOpen) &&
                        <nav className='hs-terms-toc-list'>
                            {shown.map(([num, title]) => (
                                <a key={num} href={`#${articleId(num)}`} className={active === articleId(num) ? 'on' : ''} onClick={jump(articleId(num))}>
                                    <span>{num}</span>{title}
                                </a>
                            ))}
                        </nav>}
                </aside>

                <article className='hs-terms-articles'>
                    {shown.length === 0 &&
                        <div className='hs-terms-empty'>
                            <span>{t("no_results", { query })}</span>
                            <button onClick={() => setQuery('')}>{t("show_all")}</button>
                        </div>}
                    {shown.map(([num, title, blocks]) => (
                        <section key={num} id={articleId(num)} className='hs-terms-article'>
                            <div className='hs-terms-article-head'>
                                <span>{t("article", { n: num })}</span>
                                <h2>{title}</h2>
                            </div>
                            {blocks.map((block, i) => <Block key={i} block={block} />)}
                        </section>
                    ))}
                    <div className='hs-terms-foot'>
                        <span>Halostan.rs</span>
                        <Link to='/privacy'>{t("privacy_link")} →</Link>
                    </div>
                </article>
            </div>

            {showTop &&
                <button className='hs-terms-top' onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label={t("to_top")}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m6 15 6-6 6 6" /></svg>
                </button>}
        </div>
    );
};
